/**
 * Settling identity claims — the administrator's half of docs/05 §11.4.
 *
 * Approving a claim hands over a person's proposal rights, their place in the
 * family and the photographs of their children, so it is deliberately a human
 * decision rather than a rule. The panel shows who is asking to be whom and
 * gets out of the way.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersApi } from '../../api/client.js';
import { problemMessage } from '../../ui/problem.js';
import ui from '../../ui/components.module.css';
import styles from './audit.module.css';

export function ClaimsPanel(): JSX.Element | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState<Record<string, string>>({});

  const claims = useQuery({ queryKey: ['claims'], queryFn: () => usersApi.claims() });

  const resolve = useMutation({
    mutationFn: (args: { userId: string; decision: 'approve' | 'refuse' }) =>
      usersApi.resolveClaim(args.userId, args.decision, notes[args.userId]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['claims'] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // A panel with nothing in it is noise on an admin screen.
  if (claims.isSuccess && claims.data.data.length === 0) return null;

  return (
    <section className={ui.card}>
      <h2>{t('claim.adminTitle')}</h2>
      <p className={ui.muted}>{t('claim.adminHelp')}</p>

      {claims.isPending && <p className={ui.muted}>{t('app.loading')}</p>}
      {claims.isError && <p className={ui.problem}>{problemMessage(claims.error, t)}</p>}

      {claims.data?.data.map((claim) => (
        <div key={claim.user.id} className={styles.row ?? ''}>
          <p>
            <strong>{claim.user.display_name}</strong>{' '}
            <span className={ui.muted}>{claim.user.email}</span>
            {' → '}
            <Link to={`/people/${claim.person.id}`}>{claim.person.display_label}</Link>
          </p>
          <label className={ui.field}>
            <span className={ui.label}>{t('claim.note')}</span>
            <input
              className={ui.input}
              maxLength={500}
              value={notes[claim.user.id] ?? ''}
              onChange={(event) =>
                setNotes((all) => ({ ...all, [claim.user.id]: event.target.value }))
              }
            />
          </label>
          <button
            type="button"
            className={ui.button}
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ userId: claim.user.id, decision: 'approve' })}
          >
            {t('claim.approve')}
          </button>{' '}
          <button
            type="button"
            className={ui.buttonSecondary}
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ userId: claim.user.id, decision: 'refuse' })}
          >
            {t('claim.refuse')}
          </button>
        </div>
      ))}
      {resolve.isError && <p className={ui.problem}>{problemMessage(resolve.error, t)}</p>}
    </section>
  );
}
